import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { useRootStore } from '@qsrsoft/myq-core';
import { getAPI, putAPI } from '@/helpers/rewardsApi.js';
import { excludedRoles } from '@/helpers/rewardsConstants';

export const useStore = defineStore('admin', () => {
  const snack = ref();
  const selectedUser = ref();
  const userTableDirty = ref(false);

  async function setSelectedUser(user) {
    selectedUser.value = user;
  }
  function setSnackbar({ text, timeout = 5000, color = 'success' }) {
    snack.value = { text, timeout, color };
  }

  /**
   * @param {string} text Error message to show the user
   */
  function showErrorMessage(text) {
    setSnackbar({ text, color: 'error', timeout: 60000 });
  }
  /**
   * @param {string} text Success message to show the user
   */
  function showSuccessMessage(text) {
    setSnackbar({ text, color: 'success' });
  }
  /**
   * @param {string} text Info message to show the user
   */
  function showInfoMessage(text) {
    setSnackbar({ text, color: 'primary' });
  }

  const rootStore = useRootStore();
  const { permissions, isAdmin, locations, orgId, groups } =
    storeToRefs(rootStore);

  const hasAwardPointsPermission = computed(
    () => permissions.value.includes('award_points') || isAdmin.value,
  );

  const hasSendPointsPermission = computed(() =>
    permissions.value.includes('send_points'),
  );

  const activeLocations = computed(() => {
    return locations.value
      .filter((x) => x.isActive)
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  const rewardsSettings = ref(null);
  const loadingRewardsSettings = ref(true);
  const isloadingRewardSettings = ref(null);
  let rewardsSettingsResolver;
  const rewardsSettingsPromise = new Promise((resolve) => {
    rewardsSettingsResolver = resolve;
  });
  async function fetchRewardsSettings() {
    if (rewardsSettings.value) return;
    if (isloadingRewardSettings.value) return;
    loadingRewardsSettings.value = true;
    isloadingRewardSettings.value = true;

    try {
      const params = new URLSearchParams({
        nsn: locations.value.filter((x) => x.isActive).map((l) => l.value),
        orgId: orgId.value,
      });
      const response = await getAPI(
        `reports/rewards/settings?${params.toString()}`,
      );
      rewardsSettings.value = new Proxy(response ?? {}, {
        get: (target, prop) => {
          if (prop in target) {
            if (
              prop === 'rewardsRoles' &&
              target.rewardsRoles &&
              target.rewardsRoles.length > 0
            ) {
              return target[prop].filter((role) =>
                groups.value.some((group) => group.groupId === role),
              );
            }

            return target[prop];
          }

          if (prop === 'rewardsRoles') {
            return defaultRewardsRolesIds.value;
          }
        },
      });
      rewardsSettingsResolver();
    } catch (error) {
      console.error('Error fetching rewards settings:', error);
      rewardsSettings.value = null;
      isloadingRewardSettings.value = null;
      rewardsSettingsResolver();
      throw error;
    } finally {
      loadingRewardsSettings.value = false;
    }
  }
  async function waitForRewardsSettings() {
    await rewardsSettingsPromise;
    return rewardsSettings.value;
  }

  const dailySpendingLimit = computed(() => {
    return rewardsSettings.value?.dailySpendingLimit || 0;
  });

  const dailyEGifterSpendingLimit = computed(() => {
    return rewardsSettings.value?.dailyEGifterSpendingLimit || 0;
  });

  const monthlySpendingLimit = computed(() => {
    return rewardsSettings.value?.monthlySpendingLimit || 0;
  });

  const monthlyEGifterSpendingLimit = computed(() => {
    return rewardsSettings.value?.monthlyEGifterSpendingLimit || 0;
  });

  const putRewardsSettings = async (body) => {
    const params = new URLSearchParams({
      nsn: locations.value.filter((x) => x.isActive).map((l) => l.value),
      orgId: orgId.value,
    });
    await putAPI(`reports/rewards/settings?${params.toString()}`, body);
  };

  const filteredGroups = computed(() =>
    groups.value.filter((group) => !excludedRoles.includes(group.name)),
  );

  const defaultRewardsRoleNames = [
    'General Manager',
    'Department Manager',
    'Shift Manager',
    'Crew',
    'Floor Supervisor',
  ];

  /**
   * Groups, excluding those in the excludedRoles list, and filtered down
   * to only those matching the above default list for rewards
   */
  const defaultRewardsRoles = computed(() =>
    filteredGroups?.value.filter((group) =>
      defaultRewardsRoleNames.includes(group.name),
    ),
  );

  /**
   * Just the ids from the above default rewards roles list
   */
  const defaultRewardsRolesIds = computed(() =>
    defaultRewardsRoles.value.map((role) => role.groupId),
  );

  return {
    snack,
    setSnackbar,
    showErrorMessage,
    showSuccessMessage,
    showInfoMessage,
    selectedUser,
    setSelectedUser,
    userTableDirty,
    hasAwardPointsPermission,
    hasSendPointsPermission,
    activeLocations,
    rewardsSettings,
    fetchRewardsSettings,
    putRewardsSettings,
    loadingRewardsSettings,
    waitForRewardsSettings,
    dailySpendingLimit,
    dailyEGifterSpendingLimit,
    monthlySpendingLimit,
    monthlyEGifterSpendingLimit,
    filteredGroups,
    defaultRewardsRoles,
    defaultRewardsRolesIds,
  };
});
